/* TODO: t() */
import React, { ComponentProps, useState, useCallback } from 'react';
import {
  TouchableOpacity,
  View,
  StyleSheet,
  StyleProp,
  TextStyle,
} from 'react-native';

import { hitSlop, themeColors } from './Constants';
import Icon, { IconName, IconSize } from './Icon';
import Text from './Text';
import { useTabIndex } from '../../support/rn-web/helpers/useTabIndex';

type XButtonProps = ComponentProps<typeof TouchableOpacity> & {
  color?: string;
  tabIndex?: number;
};

export type Props = ComponentProps<typeof TouchableOpacity> & {
  hoverStyle?: StyleSet;
  textStyle?: StyleProp<TextStyle>;
  textHoverStyle?: StyleProp<TextStyle>;
  title?: string;
  iconName?: IconName;
  iconColor?: string;
  iconSize?: IconSize;
  titleType?: 'regular' | 'heading' | 'label' | 'title';
  numberOfLines?: number;
  isPrimary?: boolean;
  isDisabled?: boolean;
  noBorder?: boolean;
  children?: React.ReactNode;
  id?: string;
  buttonSize?: 'normal' | 'large';
  tabIndex?: number | undefined;
};
export { Props as ButtonProps };

function Button(props: Props) {
  let {
    title,
    iconName,
    iconColor,
    iconSize = 'S',
    numberOfLines = 1,
    isPrimary = false,
    isDisabled = false,
    noBorder = false,
    style,
    hoverStyle,
    textStyle,
    textHoverStyle,
    titleType,
    children,
    id,
    buttonSize,
    testID,
    tabIndex,
    ...otherProps
  } = props;

  let [isHovered, setHovered] = useState(false);
  let onMouseEnter = useCallback(() => {
    setHovered(true);
  }, []);
  let onMouseLeave = useCallback(() => {
    setHovered(false);
  }, []);
  let ref = useTabIndex(tabIndex);

  return (
    <TouchableOpacity
      {...otherProps}
      ref={ref as never}
      testID={testID}
      accessibilityRole="button"
      style={[
        styles.root,
        !noBorder && styles.border,
        isPrimary && styles.primary,
        isDisabled && styles.disabled,
        style,
        isHovered && hoverStyle,
      ]}
      disabled={isDisabled}
      activeOpacity={0.6}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {iconName && (
        <View style={title ? { marginRight: 3 } : undefined}>
          <Icon
            name={iconName}
            size={iconSize}
            color={iconColor || themeColors.black}
          />
        </View>
      )}
      <Text
        type={titleType}
        numberOfLines={numberOfLines}
        id={id}
        style={[
          buttonSize === 'large' ? styles.buttonTextLarge : styles.buttonText,
          isPrimary && styles.buttonTextPrimary,
          isDisabled && styles.disabledButtonText,
          textStyle,
          isHovered && textHoverStyle,
        ]}
      >
        {title}
      </Text>
      {children}
    </TouchableOpacity>
  );
}

function XButton(props: XButtonProps) {
  let { color, tabIndex, ...otherProps } = props;
  let ref = useTabIndex(tabIndex);
  return (
    <TouchableOpacity
      {...otherProps}
      ref={ref as never}
      hitSlop={hitSlop}
      accessibilityRole="button"
    >
      <Text style={[styles.xText, { color }]}>×</Text>
    </TouchableOpacity>
  );
}

let styles = StyleSheet.create({
  root: {
    marginHorizontal: 2,
    backgroundColor: themeColors.white,
    paddingVertical: 6,
    paddingHorizontal: 12,
    cursor: 'pointer',
    userSelect: 'none',
    flexDirection: 'row',
    alignItems: 'center',
  },
  primary: {
    backgroundColor: themeColors.blue,
    borderColor: themeColors.blue,
  },
  disabled: {
    opacity: 0.65,
    cursor: 'not-allowed',
    backgroundColor: themeColors.white,
    borderColor: '#ccc',
    boxShadow: 'none',
  },
  border: {
    borderWidth: 1,
    borderColor: '#cccccc',
    borderRadius: 6,
  },
  buttonText: {
    color: '#333333',
    fontSize: 14,
    lineHeight: 20,
    whiteSpace: 'nowrap',
  },
  buttonTextLarge: {
    color: '#333333',
    fontSize: 20,
    lineHeight: 40,
    paddingHorizontal: 12,
    whiteSpace: 'nowrap',
  },
  buttonTextPrimary: {
    color: themeColors.white,
  },
  disabledButtonText: {
    color: '#333',
  },
  xText: {
    fontSize: 30,
  },
});

export { XButton };

export default Button;
